import { useEffect, useState } from 'react';
import { RxCollection } from 'rxdb';
import { useRxCollection } from 'rxdb-hooks';
import { Subscription } from 'rxjs';

import { mapToChecksAdminInspectionType } from '@/utilities/inspectionHelpers';

import { useMst } from '../../mobx-models/Root';
import { InspectionType } from '../../utilities/constants';
import { Inspection, InspectionDocument } from '../Inspections/queryBuilder';
import RxdbCollectionName from '../rxdbCollectionName';
import { InspectionResult, InspectionResultDocument } from './queryBuilder';

export interface InspectionResultItem {
  inspectionDetails: InspectionDocument;
  inspectionResult: InspectionResultDocument;
}

const useInspectionResults = (inspectionId?: string) => {
  const inspectionsCollection = useRxCollection(
    RxdbCollectionName.INSPECTIONS,
  ) as RxCollection<Inspection>;
  const inspectionResultsCollection = useRxCollection(
    RxdbCollectionName.INSPECTION_RESULTS,
  ) as RxCollection<InspectionResult>;
  const { shiftPicker } = useMst();

  const [inspectionsInitialized, setInspectionsInitialized] = useState(false);
  const [inspectionResultForId, setInspectionResultForId] = useState<InspectionResultDocument>();
  const [shiftCardInspectionsList, setShiftCardInspectionsList] = useState<InspectionResultItem[]>(
    [],
  );
  const [inspectionCount, setInspectionCount] = useState<number>(0);

  useEffect(() => {
    if (inspectionsCollection && inspectionResultsCollection) {
      setInspectionsInitialized(true);
    }
  }, [inspectionsCollection, inspectionResultsCollection]);

  const getInspectionDetails = (
    inspections: Map<string, InspectionDocument>,
    result: InspectionResult,
  ) => {
    let inspectionDetails = inspections.get(result.inspectionId);
    if (!inspectionDetails) {
      const parsedInspectionDetail = JSON.parse(result.inspectionDetail);
      if (parsedInspectionDetail) {
        if (parsedInspectionDetail.preOpConfig) {
          // Custom Equipment Check
          inspectionDetails = parsedInspectionDetail.preOpConfig.inspection;
        } else if (parsedInspectionDetail.areaCheckConfig) {
          // Area Check
          inspectionDetails = parsedInspectionDetail.areaCheckConfig.inspection;
        } else if (parsedInspectionDetail.cuttingPermitConfig) {
          // Cutting Permit
          inspectionDetails = parsedInspectionDetail.cuttingPermitConfig.inspection;
        } else if (parsedInspectionDetail.inspection) {
          // WFAI
          inspectionDetails = parsedInspectionDetail.inspection;
        }
      }
    }

    return inspectionDetails;
  };

  const listInspections = async (borerShiftId: string): Promise<InspectionResultItem[]> => {
    if (!inspectionsCollection) return;
    if (!inspectionResultsCollection) return;

    let inspectionResults: InspectionResultDocument[];
    try {
      inspectionResults = await inspectionResultsCollection
        .find({
          selector: {
            borerShiftId,
          },
        })
        .exec();
      if (!inspectionResults) return [];
    } catch (error) {
      console.log('🚀 ~ file: useInspectionResults.tsx ~ line 58 ~ listInspections ~ error', error);
      return;
    }

    let inspections: Map<string, InspectionDocument>;
    try {
      inspections = await inspectionsCollection
        .findByIds(inspectionResults.map(result => result.inspectionId))
        .exec();
    } catch (error) {
      console.log('🚀 ~ file: useInspectionResults.tsx ~ line 58 ~ listInspections ~ error', error);
    }

    return inspectionResults.map(result => {
      const inspectionDetails = getInspectionDetails(inspections, result);
      return {
        inspectionResult: result,
        inspectionDetails,
      };
    });
  };

  /**
   * List the PreOp Checks in this shift based on the borerShiftId
   * Also includes Borer / Post-cutting / Borer Flexiveyor checks
   *
   * @return {InspectionResultDocument[]} List of inspection results that are pre op checks
   */
  const listPreOpChecksThisShift = async (): Promise<InspectionResultItem[]> => {
    if (!inspectionResultsCollection || !shiftPicker.currentBorerShiftId || !inspectionsCollection)
      return [];

    const inspectionResults: InspectionResultDocument[] = await inspectionResultsCollection
      .find({
        selector: {
          borerShiftId: {
            $eq: shiftPicker.currentBorerShiftId,
          },
        },
        sort: [
          {
            updatedAt: 'desc',
          },
        ],
      })
      .exec();

    const inspections: Map<string, InspectionDocument> = await inspectionsCollection
      .findByIds(inspectionResults.map(result => result.inspectionId))
      .exec();

    const inspectionResultsWithInspection = inspectionResults
      .map(result => {
        const inspectionDetails = getInspectionDetails(inspections, result);
        return {
          inspectionResult: result,
          inspectionDetails,
        };
      })
      .filter(
        inspection =>
          mapToChecksAdminInspectionType(inspection.inspectionDetails?.inspectionType) ===
          InspectionType.CUSTOM_EQUIPMENT,
      );

    return inspectionResultsWithInspection;
  };

  useEffect(() => {
    let subscription: Subscription | undefined;

    if (!!inspectionResultsCollection && inspectionId) {
      subscription = inspectionResultsCollection
        .findOne({ selector: { id: { $eq: inspectionId } } })
        .$.subscribe(item => {
          setInspectionResultForId(item);
        });
    } else {
      setInspectionResultForId(undefined);
    }
    return () => {
      if (subscription && subscription.unsubscribe) subscription.unsubscribe();
    };
  }, [inspectionId, inspectionResultsCollection]);

  const getCurrrentshiftCardInspectionsList = async () => {
    if (shiftPicker.currentBorerShiftId) {
      const results = await listInspections(shiftPicker.currentBorerShiftId);
      setShiftCardInspectionsList(results);
      getInspectionCount(results);
    } else {
      setShiftCardInspectionsList([]);
      getInspectionCount([]);
    }
  };

  const getInspectionCount = (list: InspectionResultItem[]) => {
    let count = 0;
    list.forEach(inspection => {
      const { inspectionDetails } = inspection;
      const inspectionType = inspectionDetails?.inspectionType;
      const checksAdminInspectionType = mapToChecksAdminInspectionType(inspectionType);
      if (
        checksAdminInspectionType === InspectionType.WORKFACE_AREA_INSPECTION ||
        checksAdminInspectionType === InspectionType.CUSTOM_AREA_CHECK ||
        checksAdminInspectionType === InspectionType.CUTTING_PERMIT
      ) {
        count++;
      }
    });
    setInspectionCount(count);
  };

  useEffect(() => {
    if (inspectionsInitialized && inspectionResultsCollection) {
      getCurrrentshiftCardInspectionsList();
      inspectionResultsCollection.find().$.subscribe(() => {
        getCurrrentshiftCardInspectionsList();
      });
    }
  }, [shiftPicker.currentBorerShiftId, inspectionsInitialized, inspectionResultsCollection]);

  return {
    listInspections,
    inspectionsInitialized,
    inspectionResultsCollection,
    listPreOpChecksThisShift,
    inspectionResultForId,
    shiftCardInspectionsList,
    inspectionCount,
  };
};

export default useInspectionResults;
